import type {
    ConversationMessage,
    sendConversationMessage_Body,
    ConversationParticipant,
    ConversationContact,
} from '@/api';
import type { z } from 'zod';
import 'viewerjs/dist/viewer.css';
import UserDeletedAvatar from '@/assets/images/misc/deleted-avatar.png';
import { useEventListener } from '@vueuse/core';
import { ConversationModel } from '@/api/models';
import { AxiosError } from 'axios';
import { storeToRefs } from 'pinia';

export const useReactiveConversation = (conversationId: Ref<number>) => {
    const { user } = useCompany();
    const { on, actions, ...rest } = useConversation(conversationId);
    const conversationStore = useConversationStore();

    //scroll management

    const containerRef = ref<HTMLDivElement>();
    const containerScroll = useRememberScroll(containerRef);

    //store
    type MessagePayload = z.infer<typeof sendConversationMessage_Body>;
    const conversation = shallowRef<ConversationModel>();
    const contacts = shallowRef<ConversationContact[]>();
    const participants = ref<ConversationParticipant[]>([]);
    const messages = ref<ConversationMessage[]>([]);
    const temporaryMessages = ref<[temporaryId: string, payload: MessagePayload][]>([]);
    const failedMessages = ref<[payload: MessagePayload, error?: string][]>([]);
    const hasMoreMessages = ref(false);

    //computed
    const myParticipant = computed(() => participants.value.find(p => p.user_id === user.value?.id));
    const sortedMessagesWithContent = computed(
        () => messages.value?.filter(a => a.message_body || a.attachments).sort((a, b) => a.id - b.id) ?? [],
    );
    const attachments = computed(() => messages.value?.flatMap(m => m.attachments ?? []) ?? []);

    //helpers
    const messageName = (id: number | null): string => {
        const participant = participants.value?.find(p => p.user_id === id);
        return participant == null ? 'User Deleted' : `${participant.name}`;
    };

    const participant = (id: number | null) => {
        return participants.value.find(p => p.user_id === id);
    };

    const messageAvatar = (id: number | null) => {
        if (id == null) {
            return UserDeletedAvatar;
        }
        return (
            participants.value.find(p => p.user_id === id)?.avatar_url ??
            `https://picsum.photos/id/${id % 1000}/300/300`
        );
    };

    const updateReadUpto = () => {
        if (!myParticipant.value) return;
        actions.markRead({ me: myParticipant.value, messages: messages.value });
    };

    //event handlers

    const removeTemporaryMessage = (temporaryId: any) => {
        if (typeof temporaryId !== 'string') return;
        const idx = temporaryMessages.value.findIndex(t => t[0] === temporaryId);
        if (idx === -1) return;
        return temporaryMessages.value.splice(idx, 1)[0];
    };

    const mergeParticipant = (participant: ConversationParticipant) => {
        const existingIdx = participants.value.findIndex(p => p.user_id === participant.user_id);
        if (existingIdx === -1) {
            participants.value.push(participant);
        } else {
            const existing = participants.value[existingIdx];
            if (
                new Date(existing.updated_at).getTime() < new Date(participant.updated_at).getTime() ||
                (existing.read_upto_id != null &&
                    participant.read_upto_id != null &&
                    existing.read_upto_id < participant.read_upto_id)
            ) {
                participants.value[existingIdx] = participant;
            }
        }
    };

    const mergeMessage = (message: ConversationMessage) => {
        const existingIdx = messages.value.findIndex(m => m.id === message.id);
        if (existingIdx === -1) {
            messages.value.push(message);
        } else {
            const existing = messages.value[existingIdx];
            if (new Date(existing.updated_at).getTime() < new Date(message.updated_at).getTime()) {
                messages.value[existingIdx] = message;
            }
        }
    };

    on('error', e => {
        if (e instanceof AxiosError && e.response?.data?.message) {
            toast.error(e.response.data.message);
        } else {
            toast.error(e);
        }
    });
    on('state-reset', () => {
        conversation.value = undefined;
        contacts.value = undefined;
        participants.value = [];
        messages.value = [];
        hasMoreMessages.value = false;
        temporaryMessages.value = [];
        failedMessages.value = [];
        contactsSearch.value = '';
    });
    on('conversation-loaded', c => (conversation.value = new ConversationModel(c)));
    on('conversation-updated', c => (conversation.value = new ConversationModel(c)));
    on('participants-loaded', p => (participants.value = p));
    on('participant-added', p => mergeParticipant(p));
    on('participant-removed', p => mergeParticipant(p));
    on('participant-updated', p => mergeParticipant(p));
    on('contacts-loaded', c => (contacts.value = c));
    on('messages-loaded', (m, more) => {
        containerScroll.remember();
        for (const message of m) {
            mergeMessage(message);
            conversationStore.markRead(message.id);
        }
        hasMoreMessages.value = more ?? true;
        containerScroll.revert();
        if (document.hasFocus()) {
            updateReadUpto();
        }
    });
    on('message-sent', (t, b) => {
        temporaryMessages.value.push([t, b]);
    });
    on('message-created', (m, t) => {
        conversationStore.markRead(m.id);
        mergeMessage(m);
        removeTemporaryMessage(t);
        if (document.hasFocus()) {
            updateReadUpto();
        }
        containerScroll.bottom();
    });
    on('message-failed', (t, e) => {
        const removed = removeTemporaryMessage(t);
        if (removed) {
            failedMessages.value.push([removed[1], e]);
        }
        if (e) {
            toast.error(e);
        }
    });

    useEventListener(document, 'focusin', () => {
        updateReadUpto();
    });

    const contactsSearch = ref('');

    watch(contactsSearch, c => {
        if (c && !contacts.value) {
            actions.loadContacts();
        }
    });

    watch(storeToRefs(conversationStore).unreadMessageIds, () => {
        conversationStore.markRead(messages.value.map(m => m.id));
    });

    return {
        conversation,
        participants,
        messages,
        sortedMessagesWithContent,
        attachments,
        hasMoreMessages,
        temporaryMessages,
        failedMessages,
        actions,
        myParticipant,
        messageName,
        messageAvatar,
        updateReadUpto,
        mergeParticipant,
        participant,
        containerRef,
        contactsSearch,
        contacts,
        ...rest,
    };
};
